import { Fragment, useEffect, type ReactNode } from "react";
import { latLngBounds } from "leaflet";
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip, useMap } from "react-leaflet";

import { useTokens } from "../hooks/useTokens";
import type { DadosPiloto } from "../hooks/usePiloto";
import { corDaBoia } from "../lib/cores";
import { frescura } from "../lib/format";
import { faseNoInstante, ultimoAte, type Piloto } from "../lib/pilotos";
import type { NodeState, PositionPoint } from "../types";

/** Cores de estado. Reservadas — nunca servem de cor de série. */
const COR: Record<ReturnType<typeof frescura>, string> = {
  boa: "#0ca30c",
  aviso: "#fab219",
  critica: "#d03b3b",
};

/**
 * Enquadramento de abertura: a foz inteira — molhes, marina e praias — e não
 * só o pentágono sobre água lisa. É a costa à volta que diz *onde* as boias
 * estão; o detalhe fica a dois níveis de scroll.
 *
 * Sem nós usa-se a caixa fixa da foz; com nós, a caixa que os contém (o N1
 * na marina incluído — um centro médio com zoom fixo cortava-o). Na prática
 * abre quase sempre a caixa fixa: o mapa monta antes de a frota chegar e o
 * MapContainer é imutável depois de montar.
 */
function limites(nos: NodeState[]): [[number, number], [number, number]] {
  if (nos.length === 0) {
    return [
      [41.6765, -8.849],
      [41.687, -8.838],
    ];
  }
  const lats = nos.map((n) => Number(n.latitude));
  const lons = nos.map((n) => Number(n.longitude));
  return [
    [Math.min(...lats), Math.min(...lons)],
    [Math.max(...lats), Math.max(...lons)],
  ];
}

const paraPar = (p: PositionPoint): [number, number] => [
  Number(p.latitude),
  Number(p.longitude),
];

/**
 * Reencaixa o mapa quando os rastos do piloto chegam.
 *
 * O `bounds` do MapContainer é imutável depois de montar, e o rasto do dia do
 * piloto sai da foz (a viagem começa rio acima) — sem isto abria-se o replay
 * e a história ficava fora do ecrã.
 */
function EnquadrarPiloto({
  id,
  pontos,
}: {
  id: string;
  pontos: Array<[number, number]>;
}): null {
  const mapa = useMap();
  // Pela contagem, não por "há pontos": a posição planeada (da frota) chega
  // antes dos rastos (da API do piloto), e encaixar só na primeira leva
  // deixava o mapa colado a um ponto único com os rastos fora do ecrã.
  const contagem = pontos.length;

  useEffect(() => {
    if (contagem > 0) mapa.fitBounds(latLngBounds(pontos), { padding: [40, 40] });
    // Reencaixa quando chegam pontos novos — não a cada arrasto do cursor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, contagem, mapa]);

  return null;
}

export interface CamadaPiloto {
  def: Piloto;
  dados: DadosPiloto;
  /** Instante do cursor do replay, em ms de época. */
  instante: number;
}

interface Props {
  nos: NodeState[];
  selecionado: string | null;
  /** Rasto GNSS da boia selecionada no intervalo. Vazio = sem fixes. */
  rasto: PositionPoint[];
  aoSelecionar: (code: string) => void;
  /** Modo piloto: substitui os marcadores ao vivo pelo replay. */
  piloto?: CamadaPiloto | null;
}

export function FleetMap({ nos, selecionado, rasto, aoSelecionar, piloto = null }: Props): ReactNode {
  const escuro = useTokens()["--modo"] === "dark";

  return (
    <MapContainer
      className="mapa"
      bounds={limites(nos)}
      boundsOptions={{ padding: [40, 40] }}
      scrollWheelZoom
      preferCanvas={false}
    >
      <TileLayer
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        maxZoom={19}
      />

      {/*
        Rasto GNSS da boia selecionada.

        Desenha-se **antes** dos marcadores, para passar por baixo deles. Na cor
        da boia, não na de estado: isto é a identidade dela, não a saúde.

        Sem fixes no intervalo não se desenha nada. O mapa não pode inventar um
        rasto a partir da posição planeada — seria uma linha que diz "esteve
        aqui" sobre uma boia que nunca reportou onde estava.
      */}
      {piloto === null && selecionado !== null && rasto.length > 1 && (
        <Polyline
          positions={rasto.map(paraPar)}
          pathOptions={{
            color: corDaBoia(selecionado, escuro),
            weight: 2,
            opacity: 0.75,
          }}
        />
      )}

      {piloto === null &&
        nos.map((no) => {
          const estado = frescura(no);
          const planeada = no.position_source === "planned";
          const activo = no.code === selecionado;

          return (
            <CircleMarker
              key={no.code}
              center={[Number(no.latitude), Number(no.longitude)]}
              radius={activo ? 13 : 11}
              pathOptions={{
                color: COR[estado],
                // Tracejado quando o ponto é a posição **planeada**, não um fix
                // do GNSS. Sem isto o mapa dizia "está aqui" sobre uma boia que
                // nunca reportou posição nenhuma.
                dashArray: planeada ? "3 3" : undefined,
                weight: activo ? 3 : 2,
                fillColor: COR[estado],
                fillOpacity: activo ? 0.55 : 0.3,
              }}
              eventHandlers={{ click: () => aoSelecionar(no.code) }}
            >
              {/*
                Um só Tooltip por marcador — o react-leaflet ignora o segundo, e
                com dois o que ficava visível era a caixa de detalhe, sempre
                aberta, a tapar o mapa.

                Este é o rótulo da marca: o código, permanente. A identidade nunca
                é só a cor. O detalhe está no painel, a um clique.
              */}
              <Tooltip permanent direction="center" className="rotulo-marcador">
                {no.code}
              </Tooltip>
            </CircleMarker>
          );
        })}

      {/*
        Modo piloto: cada boia do elenco com o rasto **até ao cursor** e o
        marcador no último fix conhecido. As mesmas regras de honestidade do
        modo ao vivo: rasto só com fixes reais, tracejado quando a posição é a
        planeada (N2 nunca reportou onde estava), e nada no mapa antes de a
        boia ter dado sinal.
      */}
      {piloto !== null && (
        <>
          <EnquadrarPiloto
            id={piloto.def.id}
            pontos={[
              ...piloto.def.boias.flatMap((b) =>
                (piloto.dados.por[b.code]?.rasto ?? []).map(paraPar),
              ),
              ...piloto.def.boias.flatMap((b): Array<[number, number]> => {
                if (b.gnss) return [];
                const no = nos.find((n) => n.code === b.code);
                return no === undefined
                  ? []
                  : [[Number(no.planned_latitude), Number(no.planned_longitude)]];
              }),
            ]}
          />

          {piloto.def.boias.map((boia) => {
            const cor = corDaBoia(boia.code, escuro);
            const fase = faseNoInstante(boia, piloto.instante);
            const activo = boia.code === selecionado;
            const rastoBoia = piloto.dados.por[boia.code]?.rasto ?? [];
            const percorrido = rastoBoia.filter(
              (p) => Date.parse(p.timestamp) <= piloto.instante,
            );
            const fix = percorrido.length > 0 ? percorrido[percorrido.length - 1] : null;

            // Sem GNSS, a única posição defensável é a planeada, tracejada, e
            // só a partir da imersão — antes disso a boia andava de carrinha e
            // de barco, e a planeada seria um "esteve aqui" inventado.
            let centro: [number, number] | null = null;
            let planeada = false;
            if (fix !== null && fix !== undefined) {
              centro = paraPar(fix);
            } else if (!boia.gnss && (fase === "na-agua" || fase === "morta")) {
              const no = nos.find((n) => n.code === boia.code);
              if (no !== undefined) {
                centro = [Number(no.planned_latitude), Number(no.planned_longitude)];
                planeada = true;
              }
            }

            const imersaoMs = boia.imersao === null ? null : Date.parse(boia.imersao);
            const fixImersao =
              imersaoMs === null
                ? null
                : ultimoAte(rastoBoia, (p) => Date.parse(p.timestamp), imersaoMs);

            return (
              <Fragment key={boia.code}>
                {percorrido.length > 1 && (
                  <Polyline
                    positions={percorrido.map(paraPar)}
                    pathOptions={{ color: cor, weight: 2, opacity: 0.75 }}
                  />
                )}

                {/* Onde entrou na água: um anel pequeno na cor da boia. */}
                {fixImersao !== null && imersaoMs !== null && piloto.instante >= imersaoMs && (
                  <CircleMarker
                    center={paraPar(fixImersao)}
                    radius={5}
                    pathOptions={{ color: cor, weight: 2, fillOpacity: 0 }}
                  >
                    <Tooltip direction="top">{boia.code} entra na água</Tooltip>
                  </CircleMarker>
                )}

                {centro !== null && (
                  <CircleMarker
                    center={centro}
                    radius={activo ? 13 : 11}
                    pathOptions={{
                      color: fase === "morta" ? COR.critica : COR.boa,
                      dashArray: planeada ? "3 3" : undefined,
                      weight: activo ? 3 : 2,
                      fillColor: fase === "morta" ? COR.critica : COR.boa,
                      fillOpacity: activo ? 0.55 : 0.3,
                    }}
                    eventHandlers={{ click: () => aoSelecionar(boia.code) }}
                  >
                    <Tooltip permanent direction="center" className="rotulo-marcador">
                      {boia.code}
                    </Tooltip>
                  </CircleMarker>
                )}
              </Fragment>
            );
          })}
        </>
      )}
    </MapContainer>
  );
}
